import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  MagnifyingGlass, ShieldCheck, FileText, ChartLineUp,
  CheckCircle, ArrowRight, Phone, Calendar, Users,
  Warning, ClipboardText, Scales, Target, Medal
} from "@phosphor-icons/react";

const HospiceAuditsPage = () => {
  const [showCTA, setShowCTA] = useState(false);

  const auditTypes = [
    {
      title: "ADR (Additional Documentation Request)",
      description: "Medicare Administrative Contractors request additional documentation to verify medical necessity and eligibility for hospice services.",
      icon: FileText,
      tips: ["Respond within 45 days", "Include all relevant clinical documentation", "Document terminal prognosis clearly"]
    },
    {
      title: "TPE (Targeted Probe and Educate)",
      description: "CMS program targeting providers with high claim error rates. Three rounds of review with education between rounds.",
      icon: Target,
      tips: ["Review denied claims carefully", "Implement corrective actions", "Attend MAC educational sessions"]
    },
    {
      title: "UPIC/ZPIC Audits",
      description: "Unified Program Integrity Contractors investigate potential fraud, waste, and abuse in Medicare claims.",
      icon: Scales,
      tips: ["Maintain complete documentation", "Respond promptly to requests", "Consider legal consultation"]
    },
    {
      title: "RAC (Recovery Audit Contractor)",
      description: "Post-payment reviews to identify improper payments and recover overpayments.",
      icon: ChartLineUp,
      tips: ["Track appeal deadlines", "Document medical necessity", "Monitor for patterns"]
    }
  ];

  const services = [
    "Pre-audit readiness assessments",
    "ADR response preparation and review",
    "TPE round preparation and support",
    "Appeal writing and submission",
    "Documentation improvement training",
    "Compliance program development",
    "Mock audit exercises",
    "Ongoing audit monitoring"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Hospice Audit Services | ADR, TPE, RAC Audit Support | Noble Hospice Solutions</title>
        <meta name="description" content="Expert hospice audit support for ADR, TPE, UPIC, and RAC audits. We help hospice agencies prepare for audits, respond to documentation requests, and win appeals. Free consultation available." />
        <meta name="keywords" content="hospice audits, hospice ADR, hospice TPE, hospice RAC audit, Medicare audit hospice, hospice compliance audit, hospice documentation audit, ADR response hospice" />
        <link rel="canonical" href="https://noblehospicesolutions.com/hospice-audits" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Hospice Audit Support Services",
            "provider": {
              "@type": "Organization",
              "name": "Noble Hospice Solutions",
              "url": "https://noblehospicesolutions.com"
            },
            "description": "Expert hospice audit support including ADR response, TPE preparation, and appeal services.",
            "areaServed": "United States",
            "serviceType": ["Healthcare Consulting", "Compliance Consulting", "Audit Support"]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/20 text-cyan-400 text-sm font-semibold mb-6">
              <ShieldCheck weight="fill" className="w-4 h-4" />
              Expert Audit Support
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-white font-bold mb-6">
              Hospice Audit Services
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                ADR, TPE & RAC Support
              </span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Don't let Medicare audits threaten your hospice. Our experts help you prepare for, 
              respond to, and win audits with proven strategies and documentation excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/schedule-demo"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <Calendar weight="bold" className="w-5 h-5" />
                Free Audit Assessment
              </Link>
              <a
                href="tel:+17183046214"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                <Phone weight="bold" className="w-5 h-5" />
                Call (718) 304-6214
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Audit Types Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl text-slate-900 font-bold mb-4">
              Types of Hospice Audits We Handle
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Whether you're facing an ADR, TPE review, or UPIC investigation, we have the expertise to guide you through.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {auditTypes.map((audit, index) => (
              <motion.div
                key={audit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:border-cyan-500/50 transition-all"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-6">
                  <audit.icon weight="fill" className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900 mb-3">{audit.title}</h3>
                <p className="text-slate-600 mb-4">{audit.description}</p>
                <div className="space-y-2">
                  {audit.tips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle weight="fill" className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700">{tip}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl text-white font-bold mb-6">
                Comprehensive Audit Support Services
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                From pre-audit preparation to appeal submission, we provide end-to-end support 
                to protect your hospice from audit-related risks and revenue loss.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((service, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle weight="fill" className="w-5 h-5 text-cyan-400" />
                    <span className="text-slate-200">{service}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="font-display text-2xl text-white font-bold mb-6">
                Why Choose Noble for Audit Support?
              </h3>
              <div className="space-y-4">
                {[
                  { stat: "95%", label: "Appeal Success Rate" },
                  { stat: "100+", label: "Hospices Supported" },
                  { stat: "24hr", label: "Response Time" },
                  { stat: "20+", label: "Years Combined Experience" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                      {item.stat}
                    </div>
                    <div className="text-slate-300">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-blue-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white font-bold mb-6">
            Facing a Hospice Audit? Get Expert Help Now
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Don't wait until it's too late. Our audit specialists can help you respond effectively 
            and protect your hospice from costly penalties.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/schedule-demo"
              className="bg-white text-cyan-600 font-bold px-8 py-4 rounded-full hover:shadow-xl transition-all inline-flex items-center justify-center gap-2"
            >
              Schedule Free Consultation
              <ArrowRight weight="bold" className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="bg-white/20 text-white font-bold px-8 py-4 rounded-full hover:bg-white/30 transition-all"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HospiceAuditsPage;
